/**
 * Curated informational content for CLI-based connectors.
 *
 * CLI toolsets (those with a `cli_name`) no longer expose a flat list of
 * per-tool action/trigger cards on their detail page. Instead they render an
 * informational/capability view that explains, in plain language, what the
 * connector lets the agent do and gives example prompts a user can ask.
 *
 * Content is keyed by `cli_name`. Any CLI toolset without a curated entry falls
 * back to content derived from its existing description / categories, so new
 * CLI toolsets render sensibly with zero extra work here.
 */

import type { IntegrationEnhanced } from "@/api/integrations";
import {
  BarChart3,
  CalendarDays,
  CalendarPlus,
  FileSearch,
  FileText,
  FolderOpen,
  Inbox,
  ListChecks,
  type LucideIcon,
  MessageSquare,
  NotebookPen,
  PenLine,
  Search,
  Send,
  Share2,
  SquarePen,
  Table2,
  Users,
  Video,
  Zap,
} from "lucide-react";

export interface CliConnectorCapability {
  /** A small glyph that anchors the capability visually. */
  icon: LucideIcon;
  /** Short capability label, e.g. "Messaging". */
  title: string;
  /** One-line description of what the capability covers. */
  description: string;
}

export interface CliConnectorContent {
  /** One-sentence summary of what the connector unlocks for the agent. */
  tagline: string;
  /** Grouped capability highlights shown as a hairline grid. */
  capabilities: CliConnectorCapability[];
  /** Natural-language prompts the user can ask the agent. */
  examples: string[];
}

export const CLI_CONNECTOR_CONTENT: Record<string, CliConnectorContent> = {
  "slack-mcp-cli": {
    tagline:
      "Let your agent work in Slack — sending messages, finding conversations, and keeping you looped.",
    capabilities: [
      {
        icon: Send,
        title: "Messaging",
        description:
          "Send and schedule messages, reply in threads, and react across channels and DMs.",
      },
      {
        icon: Search,
        title: "Search & catch up",
        description:
          "Find messages, surface mentions, and summarize busy channels or unread conversations.",
      },
      {
        icon: Users,
        title: "Channels & people",
        description:
          "Create channels, manage membership, and look up teammates and their status.",
      },
    ],
    examples: [
      "Summarize my unread DMs from today",
      "Post the standup notes to #engineering",
      "Find the thread where we discussed the launch date",
      "Set my Slack status to heads-down until 3pm",
    ],
  },
  "gmail-mcp-cli": {
    tagline:
      "Let your agent triage, draft, and send email on your behalf in Gmail.",
    capabilities: [
      {
        icon: Inbox,
        title: "Read & triage",
        description:
          "Search your inbox, summarize threads, and surface what needs a reply.",
      },
      {
        icon: Send,
        title: "Compose & send",
        description:
          "Draft, reply to, and send emails with the right tone and context.",
      },
      {
        icon: FolderOpen,
        title: "Organize",
        description:
          "Label, archive, and manage messages to keep your inbox under control.",
      },
    ],
    examples: [
      "Summarize my unread emails from this morning",
      "Draft a reply to the latest email from Priya",
      "Find the invoice attachment from Acme last month",
      "Archive everything from the newsletter sender",
    ],
  },
  gh: {
    tagline:
      "Let your agent work across your GitHub repositories — issues, pull requests, and reviews.",
    capabilities: [
      {
        icon: ListChecks,
        title: "Issues & PRs",
        description:
          "Open, search, comment on, and update issues and pull requests.",
      },
      {
        icon: MessageSquare,
        title: "Code review",
        description:
          "Summarize diffs, leave review comments, and track what's waiting on you.",
      },
      {
        icon: FolderOpen,
        title: "Repos & workflows",
        description:
          "Inspect repositories, check workflow runs, and manage releases.",
      },
    ],
    examples: [
      "List my open pull requests that need review",
      "Summarize the changes in PR #482",
      "Open an issue for the bug we just discussed",
      "Which workflow runs failed on main today?",
    ],
  },
  "notion-mcp-cli": {
    tagline:
      "Let your agent find, read, and update pages across your Notion workspace.",
    capabilities: [
      {
        icon: Search,
        title: "Search & read",
        description:
          "Find pages and databases and pull their content into the conversation.",
      },
      {
        icon: SquarePen,
        title: "Create & edit",
        description:
          "Draft new pages, append notes, and update existing content.",
      },
      {
        icon: Table2,
        title: "Organize",
        description:
          "Work with databases, properties, and structure across your workspace.",
      },
    ],
    examples: [
      "Find the project brief for the Q3 launch",
      "Create a meeting notes page for today's sync",
      "Add the action items from this chat to my tasks database",
      "Summarize the latest changes to the roadmap page",
    ],
  },
  "granola-mcp-cli": {
    tagline:
      "Let your agent pull in your Granola meeting notes and transcripts.",
    capabilities: [
      {
        icon: NotebookPen,
        title: "Meeting notes",
        description: "Find and read notes from your recent meetings and calls.",
      },
      {
        icon: ListChecks,
        title: "Summaries & follow-ups",
        description:
          "Summarize discussions and surface decisions and action items.",
      },
      {
        icon: Search,
        title: "Search",
        description:
          "Search across past meetings for topics, people, or dates.",
      },
    ],
    examples: [
      "Summarize my last meeting with the design team",
      "What action items came out of today's calls?",
      "Find the meeting where we discussed pricing",
      "Pull the notes from yesterday's customer call",
    ],
  },
  "zoom-mcp-cli": {
    tagline:
      "Let your agent find your Zoom recordings and read meeting transcripts.",
    capabilities: [
      {
        icon: Video,
        title: "Find recordings",
        description: "Search cloud recordings by date range and meeting topic.",
      },
      {
        icon: FileText,
        title: "Read transcripts",
        description:
          "Pull the transcript from a meeting to answer questions or summarize.",
      },
      {
        icon: Search,
        title: "Search",
        description: "Locate the right recording across your recent meetings.",
      },
    ],
    examples: [
      "Find my recording from yesterday's client call",
      "What did we decide in the Tuesday standup recording?",
      "Summarize the transcript from this week's all-hands",
      "Pull the recording link for last Monday's demo",
    ],
  },
  "google-sheets-mcp-cli": {
    tagline: "Let your agent read, analyze, and update your Google Sheets.",
    capabilities: [
      {
        icon: BarChart3,
        title: "Read & analyze",
        description:
          "Pull values, summarize ranges, and answer questions about your data.",
      },
      {
        icon: PenLine,
        title: "Write & update",
        description:
          "Add rows, update cells, and populate sheets from a conversation.",
      },
      {
        icon: Search,
        title: "Find sheets",
        description: "Locate the right spreadsheet and tab across your Drive.",
      },
    ],
    examples: [
      "What's the total in the Q2 budget sheet?",
      "Add a new row to my expenses tracker",
      "Find the spreadsheet with this month's signups",
      "Summarize the trends in the sales tab",
    ],
  },
  "google-docs-mcp-cli": {
    tagline: "Let your agent find, read, and draft in your Google Docs.",
    capabilities: [
      {
        icon: FileText,
        title: "Read & summarize",
        description:
          "Open documents and pull their content into the conversation.",
      },
      {
        icon: SquarePen,
        title: "Draft & edit",
        description: "Create new docs and append or revise existing content.",
      },
      {
        icon: FileSearch,
        title: "Find docs",
        description: "Locate the right document across your Drive.",
      },
    ],
    examples: [
      "Summarize the latest draft of the proposal doc",
      "Create a doc with the notes from this conversation",
      "Find the spec we wrote for the onboarding flow",
      "Add a conclusion section to the report",
    ],
  },
  "google-drive-mcp-cli": {
    tagline: "Let your agent search and manage files across your Google Drive.",
    capabilities: [
      {
        icon: Search,
        title: "Search & find",
        description: "Locate files and folders by name, type, or content.",
      },
      {
        icon: Share2,
        title: "Read & share",
        description: "Open file contents and surface links to share.",
      },
      {
        icon: FolderOpen,
        title: "Organize",
        description: "Move, rename, and tidy files and folders.",
      },
    ],
    examples: [
      "Find the contract PDF from last quarter",
      "What files were shared with me this week?",
      "Locate the folder for the marketing assets",
      "Share a link to the latest deck",
    ],
  },
  "google-calendar-mcp-cli": {
    tagline:
      "Let your agent check your schedule and manage events in Google Calendar.",
    capabilities: [
      {
        icon: CalendarDays,
        title: "Check your schedule",
        description: "See what's coming up and find free time.",
      },
      {
        icon: CalendarPlus,
        title: "Create & update events",
        description: "Schedule meetings, invite guests, and reschedule events.",
      },
      {
        icon: Search,
        title: "Find events",
        description: "Search past and upcoming events by topic or attendee.",
      },
    ],
    examples: [
      "What's on my calendar tomorrow?",
      "Schedule a 30-minute sync with Sam on Thursday",
      "Find my next meeting with the finance team",
      "Move my 2pm to later this afternoon",
    ],
  },
  "outlook-mail-mcp-cli": {
    tagline:
      "Let your agent triage, draft, and send email in Microsoft Outlook.",
    capabilities: [
      {
        icon: Inbox,
        title: "Read & triage",
        description:
          "Search your inbox, summarize threads, and surface what needs a reply.",
      },
      {
        icon: Send,
        title: "Compose & send",
        description: "Draft, reply to, and send emails with the right context.",
      },
      {
        icon: FolderOpen,
        title: "Organize",
        description:
          "File, flag, and manage messages to stay on top of your inbox.",
      },
    ],
    examples: [
      "Summarize my unread Outlook emails",
      "Draft a reply to the latest message from the client",
      "Find the email with the signed agreement",
      "Flag everything from my manager for follow-up",
    ],
  },
  "outlook-calendar-mcp-cli": {
    tagline:
      "Let your agent check your schedule and manage events in Outlook Calendar.",
    capabilities: [
      {
        icon: CalendarDays,
        title: "Check your schedule",
        description: "See what's coming up and find open time.",
      },
      {
        icon: CalendarPlus,
        title: "Create & update events",
        description:
          "Schedule meetings, invite attendees, and reschedule events.",
      },
      {
        icon: Search,
        title: "Find events",
        description: "Search past and upcoming events by topic or attendee.",
      },
    ],
    examples: [
      "What's on my Outlook calendar today?",
      "Set up a meeting with the project team next week",
      "Find my next one-on-one",
      "Reschedule my 4pm to tomorrow morning",
    ],
  },
};

/**
 * Title-case a category/tag slug for display, e.g. "project-management" ->
 * "Project Management".
 */
function humanizeTag(tag: string): string {
  return tag
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Resolve informational content for a CLI connector. Returns the curated entry
 * keyed by `cli_name` when present, otherwise builds a generic fallback from the
 * integration's description and categories so any CLI toolset renders sensibly.
 */
export function getCliConnectorContent(
  integration: IntegrationEnhanced,
): CliConnectorContent {
  const curated = integration.cli_name
    ? CLI_CONNECTOR_CONTENT[integration.cli_name]
    : undefined;
  if (curated) {
    return curated;
  }

  const name = integration.name;
  const categories = integration.metadata?.categories ?? [];

  const capabilities: CliConnectorCapability[] =
    categories.length > 0
      ? categories.slice(0, 3).map((category) => ({
          icon: Zap,
          title: humanizeTag(category),
          description: `${name} ${humanizeTag(
            category,
          ).toLowerCase()} capabilities, available to your agents.`,
        }))
      : [
          {
            icon: Zap,
            title: "Connected actions",
            description:
              integration.description ||
              `Let your agent take actions in ${name} on your behalf.`,
          },
        ];

  return {
    tagline:
      integration.description || `Let your agent work directly with ${name}.`,
    capabilities,
    examples: [
      `Summarize my recent activity in ${name}`,
      `Find something specific in ${name}`,
      `Take an action in ${name} from this conversation`,
    ],
  };
}
